import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Popover, Card, Button, Form } from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormInput from '../../global/Forms/FormInput';
import { createAPIToken, fetchAccountAPITokens } from '../../../actions/distribution_apisettings';
import { intercomEvent } from '../../../utils/IntercomUtils';

type ApiSettingsAddNewTokenPopoverProps = {
  canCreateApiToken: boolean;
  fetchingLogsAndToken: boolean;
  fetchingAccountAPITokens: boolean;
};

const ApiSettingsAddNewTokenPopover: React.FC<ApiSettingsAddNewTokenPopoverProps> = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [popoverVisible, setPopoverVisible] = React.useState(false);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('validation:required')),
  });

  const formMethods = useForm({
    defaultValues: {
      name: '',
    },
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const { handleSubmit, formState, reset } = formMethods;

  React.useEffect(() => {
    if (popoverVisible) reset({ name: '' });
  }, [popoverVisible, reset]);

  const onSubmit = async (values: { name: string }) => {
    try {
      await dispatch(createAPIToken(values.name));
      intercomEvent('viewed-api', {
        location: 'settings',
        action: 'token-new',
      });

      dispatch(fetchAccountAPITokens());
      setPopoverVisible(false);
    } catch (error) {
      /* empty */
    }
  };

  const newTokenDescriptionContent = () => (
    <Card className="api-settings__add-token-popover" variant="borderless">
      <FormProvider {...formMethods}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <FormInput
            name="name"
            label={t('api:tokenDescriptionTitle')}
            maxLength={100}
            required
            useHookForm
          />
          <div className="flex-col items-end">
            <div>
              <Button
                onClick={() => setPopoverVisible(false)}
                className="drawer-submit__bottom-cancel"
              >
                {t('common:cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                disabled={formState.isSubmitting}
                loading={formState.isSubmitting}
              >
                {t('common:save')}
              </Button>
            </div>
          </div>
        </Form>
      </FormProvider>
    </Card>
  );

  return (
    <Popover
      open={popoverVisible}
      onOpenChange={visible => {
        setPopoverVisible(visible);
      }}
      content={newTokenDescriptionContent()}
      title={t('api:alertText')}
      trigger="click"
    >
      <Button
        type="primary"
        disabled={
          !props.canCreateApiToken || props.fetchingLogsAndToken || props.fetchingAccountAPITokens
        }
      >
        {t('api:createNewApiToken')}
      </Button>
    </Popover>
  );
};

export default ApiSettingsAddNewTokenPopover;
